/**
 * SEOBreakdowns — 2x2 grid de 4 cards do módulo SEO.
 *
 * Cards:
 *   [Canais de Origem]   [Páginas]
 *   [Dispositivos]       [Ranking de Cidades]
 *
 * - Canais de Origem: origem real de entrada (referer_entrada).
 * - Dispositivos: OS exato (Android/iPhone/Windows/Mac/Linux) via osBreakdown.
 * - Ranking de Cidades: top cidades por volume da métrica ativa.
 *
 * Todos consomem o shape `{ name, visits, percentage }` produzido pelo
 * backend (/api/seo/widgets). Cada item exibe percentual + valor absoluto.
 */

import Icon from './icons.jsx'

function fmtInt(n) {
  return Number(n || 0).toLocaleString('pt-BR')
}

function BarRow({ name, percentage, visits, label = 'usuários' }) {
  return (
    <div className="bw-bar-row">
      <div className="bw-bar-meta">
        <span className="bw-bar-name" title={name}>{name}</span>
        <span className="bw-bar-pct">
          {percentage}% <span className="bw-bar-abs">({fmtInt(visits)} {label})</span>
        </span>
      </div>
      <div className="bw-bar-track">
        <div className="bw-bar-fill" style={{ width: `${Math.min(100, percentage)}%` }} />
      </div>
    </div>
  )
}

function RankRow({ name, percentage, visits }) {
  return (
    <div className="seo-rank-row">
      <div className="seo-rank-body">
        <div className="seo-rank-head">
          <span className="seo-rank-name" title={name}>{name}</span>
          <span className="seo-rank-pct">
            {percentage}% <span className="seo-rank-abs">({fmtInt(visits)} usuários)</span>
          </span>
        </div>
        <div className="bw-bar-track">
          <div className="bw-bar-fill" style={{ width: `${Math.min(100, percentage)}%` }} />
        </div>
      </div>
    </div>
  )
}

function Card({ icon, title, subtitle, children, empty }) {
  return (
    <div className="bw-panel seo-card">
      <div className="bw-panel-head">
        <Icon n={icon} s={14} />
        <span>{title}</span>
        {subtitle && <span className="bw-map-total">{subtitle}</span>}
      </div>
      <div className="bw-panel-body">
        {(!children || (Array.isArray(children) && children.length === 0))
          ? <div className="bw-panel-empty">{empty || 'Sem dados'}</div>
          : children}
      </div>
    </div>
  )
}

function shortenUrl(url) {
  if (!url) return '—'
  // Remove scheme p/ leitura compacta
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '')
}

export default function SEOBreakdowns({
  sources = [],
  devices = [],
  pages = [],
  cities = [],
  loading = false,
  sourcesTitle = 'Canais de Origem',
  sourcesIcon = 'link',
}) {
  const sortedSources = [...sources].sort((a, b) => (b.visits || 0) - (a.visits || 0))
  return (
    <div className={`seo-breakdowns-grid ${loading ? 'is-loading' : ''}`}>
      {/* 1. Canais de Origem / Campanhas */}
      <Card icon={sourcesIcon} title={sourcesTitle}>
        {sortedSources.length > 0
          ? sortedSources.map((s) => (
              <BarRow
                key={s.name}
                name={s.name}
                percentage={s.percentage}
                visits={s.visits}
              />
            ))
          : []}
      </Card>

      {/* 2. Páginas */}
      <Card icon="page" title="Páginas mais acessadas">
        {pages.length > 0
          ? pages.map((p) => (
              <BarRow
                key={p.name}
                name={shortenUrl(p.name)}
                percentage={p.percentage}
                visits={p.visits}
                label="acessos"
              />
            ))
          : []}
      </Card>

      {/* 3. Dispositivos */}
      <Card icon="device" title="Dispositivos">
        {devices.length > 0
          ? devices.map((d) => (
              <BarRow
                key={d.name}
                name={d.name}
                percentage={d.percentage}
                visits={d.visits}
              />
            ))
          : []}
      </Card>

      {/* 4. Ranking de Cidades */}
      <Card icon="geo" title="Ranking de Cidades">
        {cities.length > 0
          ? cities.map((c) => (
              <RankRow
                key={c.name}
                name={c.name}
                percentage={c.percentage}
                visits={c.visits}
              />
            ))
          : []}
      </Card>
    </div>
  )
}
